import { FC } from 'react';
import Icon, { IconNames } from 'components/Icon';
import { tableButton } from 'components/CustomTable';
import { showToast } from 'components/Toast';
import { ENV_CONFIG } from 'config/environment';
import colors from 'constants/colors';
import { copyToClipboard } from 'helpers/clipboard';
import {
  AdminShoppingCart,
  CriteriaType,
  RecipientSelectionMode,
  ShoppingCartRecipientStatus,
} from 'store/shoppingCart/types';
import {
  CRITERIA_OPTIONS,
  PROCESSING_REDIRECT_OPTIONS,
  RECIPIENT_SELECTION_MODE_OPTIONS,
} from './ShoppingCartDetail/constants';

type Props = {
  clientId: number;
  clientName: string;
  cart: AdminShoppingCart;
  onEdit: () => void;
};

const getRecipientsSummary = (cart: AdminShoppingCart) => {
  if (cart.recipient_selection_mode === RecipientSelectionMode.CRITERIA_MATCH) {
    return CRITERIA_OPTIONS.filter(option => cart.criteria.includes(option.value as CriteriaType))
      .map(option => option.label)
      .join(', ');
  }
  const activeCount = cart.recipients.filter(
    recipient => recipient.shopping_cart_recipient_status === ShoppingCartRecipientStatus.ACTIVE
  ).length;
  return `${activeCount} active recipient${activeCount === 1 ? '' : 's'}`;
};

export const ActiveCartSummary: FC<Props> = ({ clientId, clientName, cart, onEdit }) => {
  const modeLabel = RECIPIENT_SELECTION_MODE_OPTIONS.find(
    option => option.value === cart.recipient_selection_mode
  )?.label;
  // The read side can hold processors this admin can't write (e.g. a drifted EN_GENERAL page).
  const processorLabel =
    PROCESSING_REDIRECT_OPTIONS.find(option => option.value === cart.page.processing_redirect)
      ?.label ??
    cart.page.processing_redirect ??
    'Not set';

  const details = [
    { label: 'Page Name', value: cart.page.page_name },
    { label: 'Shopping Cart Type', value: modeLabel },
    { label: 'Recipients', value: getRecipientsSummary(cart) },
    { label: 'Payment Processor', value: processorLabel },
    { label: 'Max Results', value: cart.effective_max_results },
  ];

  const publicUrl = `${ENV_CONFIG().SHOPPING_CART_PAGE}${clientId}`;

  const handleCopy = () =>
    copyToClipboard(publicUrl)
      .then(() => showToast('Link copied!', 'success'))
      .catch(() => showToast('Failed to copy link. Please copy it manually.', 'error'));

  const handleAction = (type: 'edit' | 'preview') => {
    if (type === 'edit') onEdit();
    if (type === 'preview') window.open(publicUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="shopping-cart-status-prompt">
      <span className="h4-text shopping-cart-status-prompt__body">
        {clientName} has an active shopping cart.
      </span>
      <div className="card">
        <div className="card-body shopping-cart-summary">
          <dl className="shopping-cart-summary__details">
            {details.map(({ label, value }) => (
              <div key={label} className="shopping-cart-summary__item">
                <dt className="oath-eyebrow gray">{label}</dt>
                <dd className="oath-bodysmall inkBlue">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="shopping-cart-summary__footer">
            <div className="shopping-cart-summary__item">
              <span className="oath-eyebrow gray">Website URL</span>
              <div className="url-cell">
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-text url-cell__link"
                >
                  {publicUrl}
                  <span className="sr-only"> (opens in new tab)</span>
                </a>
                <button
                  type="button"
                  className="url-cell__copy-button"
                  onClick={handleCopy}
                  aria-label={`Copy URL ${publicUrl} to clipboard`}
                >
                  <span aria-hidden="true">
                    <Icon name={IconNames.Copy} color={colors.oathBlue} />
                  </span>
                </button>
              </div>
            </div>
            <div className="shopping-cart-summary__item">
              <span className="oath-eyebrow gray">Actions</span>
              <div className="table-action-row margin0">
                {tableButton(handleAction, 'edit', null)}
                {tableButton(handleAction, 'preview', null)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
